import { Box, LinearProgress, Skeleton, Typography } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState, type JSX } from "react";

import { modelDisplayName } from "@/lib/modelLabel";

import { salesApi } from "../client.js";
import { budgetCountdown } from "./budgetCountdown.js";

/** $0.0123 under a dollar (4dp), $12.34 above (2dp). */
function usd(n: number): string {
  return `$${n < 1 ? n.toFixed(4) : n.toFixed(2)}`;
}

// Derived from the id (lib/modelLabel) — a hand-kept map printed the raw id
// for every model released after it was written.
const modelName = (m: string): string => modelDisplayName(m);

function Stat({ label, value, hint }: { label: string; value: string; hint: string }): JSX.Element {
  return (
    <Box sx={{ display: "grid", gap: 0.25, minWidth: 0 }}>
      <Typography sx={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 18, fontWeight: 700, color: "text.primary", lineHeight: 1.1 }}>
        {value}
      </Typography>
      <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{hint}</Typography>
    </Box>
  );
}

/**
 * Today's LLM spend at a glance — total cost, cost-per-lead, and a daily-
 * budget gauge — fed by GET /api/sales/v2/economics (the agent's `llm_calls`
 * ledger). Non-critical: a skeleton while loading, and hidden on error so it
 * can never block the page.
 */
export function EconomicsStrip(): JSX.Element | null {
  const q = useQuery({
    queryKey: ["sales", "economics"],
    queryFn: salesApi.getEconomics,
    refetchInterval: 30_000,
  });

  // Tick a clock so the sleep/wake countdown stays live between refetches.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  if (q.isLoading) {
    return <Skeleton variant="rounded" sx={{ height: 86, borderRadius: 1 }} />;
  }
  if (q.isError || !q.data) return null;

  const e = q.data;
  // ONE shared daily budget (2026-06-21): every sales-AI bot (Lead Generator +
  // Harvester + promotion) draws from the SAME cap, so the gauge tracks the
  // TOTAL spend vs that one cap. The per-bot split below is informational —
  // it shows WHERE the one budget went, not separate ceilings.
  //
  // The split is the SERVER's (xop-agents `llm_bots.py`), because it is a fact
  // about the ~20 caller tags and belongs next to them. This used to be
  // `harvester = harvest_enumerate + harvest_dig, lead = everything else`,
  // which billed harvest_plan_queries / harvest_consume / harvest_steer_resolve
  // / vertical_classify / email_verify to the Lead Generator. The fallback
  // reproduces the old sum only for an agent too old to send `by_bot`.
  const bots = e.by_bot;
  const harvestCost =
    bots?.["harvester"] ??
    (e.by_caller["harvest_enumerate"] ?? 0) + (e.by_caller["harvest_dig"] ?? 0);
  const leadCost = bots?.["lead_generator"] ?? Math.max(0, e.total_cost_usd - harvestCost);
  const otherCost = bots?.["other"] ?? 0;
  const pct = e.budget_usd > 0 ? Math.min(100, (e.total_cost_usd / e.budget_usd) * 100) : 0;
  const barColor: "error" | "warning" | "success" =
    e.over_budget ? "error" : pct >= 80 ? "warning" : "success";
  const models = Object.entries(e.by_model).slice(0, 3);

  // Live "sleeps in / wakes in" countdown — keyed off the TOTAL shared spend vs
  // the one budget (when it's spent, ALL the bots sleep until the reset).
  const cd = budgetCountdown({
    now,
    resetsAtMs: e.resets_at ? Date.parse(e.resets_at) : null,
    windowStartMs: e.window_start ? Date.parse(e.window_start) : null,
    spendUsd: e.total_cost_usd,
    budgetUsd: e.budget_usd,
    overBudget: e.over_budget,
  });

  return (
    <Box
      component="section"
      aria-label="Today's agent LLM spend"
      sx={{
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        boxShadow: (t: Theme) => t.shadows[1],
        px: 2,
        py: 1.5,
        display: "grid",
        gap: { xs: 1.5, sm: 3 },
        gridTemplateColumns: { xs: "1fr 1fr", sm: "auto auto minmax(0, 1fr)" },
        alignItems: "center",
      }}
    >
      <Stat label="Spend today" value={usd(e.total_cost_usd)} hint={`${e.calls} call${e.calls === 1 ? "" : "s"}`} />
      <Stat
        label="Cost / lead"
        value={e.cost_per_lead_usd != null ? usd(e.cost_per_lead_usd) : "—"}
        hint={`${e.leads_today} lead${e.leads_today === 1 ? "" : "s"} today`}
      />
      <Box sx={{ display: "grid", gap: 0.6, minWidth: 0, gridColumn: { xs: "1 / -1", sm: "auto" } }}>
        <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1 }}>
          <Typography sx={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: e.over_budget ? "error.main" : "text.disabled" }}>
            Daily budget · all sales AI{e.over_budget ? " · capped" : ""}
          </Typography>
          <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
            {usd(e.total_cost_usd)} / {usd(e.budget_usd)}
          </Typography>
        </Box>
        <LinearProgress variant="determinate" value={pct} color={barColor} sx={{ height: 8, borderRadius: 999 }} />
        {/* One shared budget — show where it went (Lead Generator vs Harvester). */}
        <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
          Lead Generator {usd(leadCost)} · Harvester {usd(harvestCost)}
          {otherCost > 0 ? ` · Unclassified ${usd(otherCost)}` : ""}
        </Typography>
        {/* Tokens burned, nothing billed ⇒ a model the gateway can't price, so
            this strip is UNDER-reporting by an unknown amount. Say so rather
            than let a confident-looking total stand in for the real bill. */}
        {e.unpriced_calls ? (
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: "warning.main" }}>
            ⚠ {e.unpriced_calls} call{e.unpriced_calls === 1 ? "" : "s"} not
            priced ({(e.unpriced_models ?? []).join(", ") || "unknown model"}) —
            real spend is higher than shown
          </Typography>
        ) : null}
        {cd.label ? (
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: cd.state === "sleeping" ? 600 : 500,
              color: cd.state === "sleeping" ? "error.main" : "text.secondary",
            }}
          >
            {cd.state === "sleeping" ? "💤 " : ""}{cd.label}
          </Typography>
        ) : null}
        {models.length > 0 ? (
          <Typography sx={{ fontSize: 11, color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {models.map(([m, c]) => `${modelName(m)} ${usd(c)}`).join("  ·  ")}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}
